import { useEffect, useState, useMemo } from "react";
import CanvasJSReact from "@canvasjs/react-charts";
import socket, { connectSocket, disconnectSocket } from "../../socket";
import { toast } from "react-toastify";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const PPPoELiveBandwidth = ({ customer }) => {
  const [chunkData, setChunkData] = useState([]);

  const [dps1, setDps1] = useState([]);
  const [dps2, setDps2] = useState([]);
  const [xVal, setXVal] = useState(0);

  const dataLength = 70;

  // Helper function to format data to Mbps or Kbps
  const formatToMbps = (value) => {
    return (value / 1024).toFixed(2); // Convert to Kbps otherwise
  };

  useEffect(() => {
    if (customer?.id) {
      connectSocket(customer?.user);

      // Request server to start stream
      socket.emit("PPPoECustomerLiveBandwidth", { customer: customer.id });

      // Handlers
      const handleBandwidthData = (data) => setChunkData(data);
      const handleBandwidthError = (err) => console.error("Error:", err);
      const handleBandwidthStopped = (data) => {
        toast.success(data);
        disconnectSocket();
      };

      // Register listeners
      socket.on("bandwidthData", handleBandwidthData);
      socket.on("bandwidthError", handleBandwidthError);
      socket.on("bandwidthStopped", handleBandwidthStopped);

      // Cleanup (remove all listeners to avoid duplicate firing)
      return () => {
        socket.off("bandwidthData", handleBandwidthData);
        socket.off("bandwidthError", handleBandwidthError);
        socket.off("bandwidthStopped", handleBandwidthStopped);
      };
    }
  }, [customer?.id]);

  useEffect(() => {
    if (chunkData.length > 0) {
      const rxValue = Number(chunkData[0]?.["rx-bits-per-second"]) || 0;
      const txValue = Number(chunkData[0]?.["tx-bits-per-second"]) || 0;

      // Update data points for both download and upload
      setDps1((prevDps1) => {
        const newDps1 = [
          ...prevDps1,
          { x: xVal, y: Number(formatToMbps(rxValue)) },
        ];
        return newDps1.length > dataLength ? newDps1.slice(1) : newDps1;
      });

      setDps2((prevDps2) => {
        const newDps2 = [
          ...prevDps2,
          { x: xVal, y: Number(formatToMbps(txValue)) },
        ];
        return newDps2.length > dataLength ? newDps2.slice(1) : newDps2;
      });

      setXVal((prevXVal) => prevXVal + 1); // Increment xVal for the next update
    }
  }, [chunkData]);

  const options = useMemo(
    () => ({
      height: window.innerHeight * 0.3,
      axisY: {
        labelFormatter: () => " ", // Hide labels
        tickLength: 0, // Remove ticks
      },
      axisX: {
        minimum: xVal - dataLength + 1, // Dynamic x-axis start point
        maximum: xVal, // Current xVal as end point
      },
      legend: {
        verticalAlign: "top", // Move legend to the top
        horizontalAlign: "center", // Center align the legend
        fontSize: 14, // Optional: Change font size
        fontWeight: "bold", // Optional: Make text bold
      },
      data: [
        {
          type: "column",
          showInLegend: true,
          legendText: `Upload: ${
            dps1[dps1.length - 1]?.y / 1024 >= 1
              ? (dps1[dps1.length - 1]?.y / 1024).toFixed(2)
              : dps1[dps1.length - 1]?.y
          } ${Number(dps1[dps1.length - 1]?.y / 1024) >= 1 ? "Mbps" : "kbps"}`,
          dataPointWidth: 2,
          dataPoints: dps1,
        },
        {
          type: "column",
          showInLegend: true,
          legendText: `Download: ${
            dps2[dps2.length - 1]?.y / 1024 >= 1
              ? (dps2[dps2.length - 1]?.y / 1024).toFixed(2)
              : dps2[dps2.length - 1]?.y
          } ${Number(dps2[dps2.length - 1]?.y / 1024) >= 1 ? "Mbps" : "kbps"}`,
          dataPointWidth: 2,
          dataPoints: dps2,
        },
      ],
    }),
    [xVal, dps1, dps2],
  );

  return (
    <div>
      <CanvasJSChart options={options} />
    </div>
  );
};

export default PPPoELiveBandwidth;
